import React, { useState, useEffect } from 'react';
import { Check, ArrowLeft, Zap, Building, Rocket, ShieldAlert, X, Clock } from 'lucide-react';
import api, { API_BASE } from '../services/api';

function Plans({ onBack }) {
  const [packages, setPackages] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [currentPlanPrice, setCurrentPlanPrice] = useState(0);
  const [currentBillingCycle, setCurrentBillingCycle] = useState('monthly');
  const [nextBillingDate, setNextBillingDate] = useState(null);
  const [showActivePeriodModal, setShowActivePeriodModal] = useState(false);

  // Retention Modal State (Churn Mitigation)
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [downgradeTarget, setDowngradeTarget] = useState(null);
  const [downgradeReason, setDowngradeReason] = useState('too_expensive');
  const [submittingDowngrade, setSubmittingDowngrade] = useState(false);
  const [downgradeSuccess, setDowngradeSuccess] = useState(null);

  // Enterprise Sales Modal State
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [enterpriseNote, setEnterpriseNote] = useState('');
  const [submittingEnterprise, setSubmittingEnterprise] = useState(false);
  const [enterpriseSuccess, setEnterpriseSuccess] = useState(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const token = localStorage.getItem('token');
        const [pkgsRes, billingRes] = await Promise.all([
          api.getPublicPackages(),
          fetch(`${API_BASE}/stripe/billing-info`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).catch(() => ({}))
        ]);
        setPackages(pkgsRes.packages || []);
        if (billingRes && billingRes.plan) {
          let planIdClean = billingRes.plan.id || 'hobby';
          if (planIdClean.startsWith('PRICE_')) planIdClean = 'pro';
          setCurrentPlanId(planIdClean);
          if (billingRes.plan.billingCycle) {
            const normCycle = (billingRes.plan.billingCycle === 'annual' || billingRes.plan.billingCycle === 'yearly') ? 'annual' : 'monthly';
            setCurrentBillingCycle(normCycle);
          }
          if (billingRes.plan.nextBillingDate) {
            setNextBillingDate(billingRes.plan.nextBillingDate);
          }
          const p = parseFloat(String(billingRes.plan.priceMonthly || '').replace(/[^0-9.]/g, ''));
          if (!isNaN(p)) setCurrentPlanPrice(p);
        }
      } catch (err) {
        console.error('Failed to fetch packages/billing:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const handleInitiateDowngrade = (pkg) => {
    setDowngradeTarget(pkg);
    setDowngradeSuccess(null);
    setShowRetentionModal(true);
  };

  const handleRequestDowngradeSupport = async () => {
    setSubmittingDowngrade(true);
    try {
      const reasonLabels = {
        too_expensive: "Too expensive for current budget",
        unused_resources: "Unused resources or slots",
        missing_features: "Missing expected features",
        project_ended: "Project ended or paused",
        other: "Other reason"
      };
      const reasonText = reasonLabels[downgradeReason] || downgradeReason;

      await api.createTicket(
        `DOWNGRADE REQUEST: ${currentPlanId || 'Current'} -> ${downgradeTarget?.tier || downgradeTarget?.id || 'Lower Plan'}`,
        'HIGH',
        `User requested a plan downgrade.\n\nTarget Plan: ${downgradeTarget?.tier || downgradeTarget?.id}\nReason: ${reasonText}\n\nManual self-service downgrades are restricted. Please review account resources and assist user with manual billing tier adjustment.`
      );

      setDowngradeSuccess({
        title: 'Request Submitted to Support',
        message: 'Your downgrade inquiry has been sent to our Support team (Ticket created). A team member will review your account resources and assist you within 24 hours.'
      });
    } catch (err) {
      alert('Failed to submit request: ' + (err.message || 'Server error'));
    } finally {
      setSubmittingDowngrade(false);
    }
  };

  const handleRequestEnterpriseSales = async () => {
    setSubmittingEnterprise(true);
    try {
      const ticketRes = await api.createTicket(
        `ENTERPRISE PLAN INQUIRY: Request for Custom Enterprise Plan`,
        'HIGH',
        `User (${currentPlanId || 'Hobby'} Tier) requested information/upgrade for Custom Enterprise Plan.\n\nUser Note / Requirements:\n${enterpriseNote || 'No additional note provided.'}\n\nPlease contact user to set up custom Enterprise pricing and features.`
      );

      const ticketId = ticketRes?.ticket?.publicId || 'Submitted';

      setEnterpriseSuccess({
        title: 'Enterprise Inquiry Submitted!',
        message: `Your inquiry (Ticket #${ticketId}) has been successfully sent to our Enterprise Sales team. A dedicated account executive will reach out to you within 24 hours.`
      });
    } catch (err) {
      alert('Failed to submit Enterprise inquiry: ' + (err.message || 'Server error'));
    } finally {
      setSubmittingEnterprise(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    const getFallbackRank = (tier) => {
      if (tier === 'enterprise') return 3;
      if (tier === 'agency' || tier === 'pro_agency') return 2;
      if (tier === 'pro') return 1;
      return 0;
    };

    const currentPkg = packages.find(p => (p.tierId || p.id) === currentPlanId) || { sortOrder: getFallbackRank(currentPlanId), price: currentPlanPrice };
    const targetPkg = packages.find(p => (p.tierId || p.id) === planId) || { sortOrder: getFallbackRank(planId), price: 0 };

    const currentRank = currentPkg.sortOrder ?? getFallbackRank(currentPlanId);
    const targetRank = targetPkg.sortOrder ?? getFallbackRank(planId);
    const currentPrice = parseFloat(currentPkg.price) || currentPlanPrice || 0;
    const targetPrice = parseFloat(targetPkg.price) || 0;

    const isCurrent = ((currentPlanId === planId) && (currentBillingCycle === billingCycle)) || (planId === 'hobby' && (!currentPlanId || currentPlanId === 'hobby'));
    const isDowngrade = !isCurrent && planId !== 'enterprise' && (targetRank < currentRank || (targetPrice < currentPrice && !isNaN(targetPrice) && !isNaN(currentPrice) && currentPrice > 0));

    if (isCurrent) {
      alert('You are already subscribed to this plan.');
      return;
    }
    if (isDowngrade) {
      handleInitiateDowngrade(targetPkg);
      return;
    }

    // Active subscription period lock: block buying a new plan until current paid cycle completes
    const isPaidPlanActive = currentPlanId && currentPlanId !== 'hobby';
    const isPeriodActive = isPaidPlanActive && nextBillingDate && (new Date() < new Date(nextBillingDate));
    if (isPeriodActive && planId !== 'enterprise') {
      setShowActivePeriodModal(true);
      return;
    }

    if (planId === 'enterprise') {
      setEnterpriseNote('');
      setEnterpriseSuccess(null);
      setShowEnterpriseModal(true);
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ plan: planId, billingCycle })
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to start upgrade session');
      }
      const data = await response.json();
      if (data.isCustom) {
        window.location.href = `mailto:sales@aibit.com?subject=Upgrade%20to%20${planId}%20Plan`;
        alert(data.message || 'Please contact sales.');
        setActionLoading(false);
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      alert(err.message);
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <div style={{ color: 'var(--text-muted)' }}>Loading plans...</div>
      </div>
    );
  }

  const getPlanIcon = (id) => {
    switch (id) {
      case 'pro': return <Zap size={24} color="var(--primary)" />;
      case 'enterprise': return <Building size={24} color="var(--accent, #a855f7)" />;
      default: return <Rocket size={24} color="var(--text-muted)" />;
    }
  };

  const getPlanStyle = (id) => {
    switch (id) {
      case 'pro':
        return {
          bg: 'linear-gradient(180deg, rgba(var(--primary-rgb), 0.08) 0%, transparent 100%), var(--surface-2)',
          border: '2px solid var(--primary)',
          shadow: '0 8px 30px rgba(var(--primary-rgb), 0.2)',
          badge: 'Most Popular',
          badgeBg: 'var(--primary)',
          textColor: 'var(--text-main)'
        };
      case 'enterprise':
        return {
          bg: 'linear-gradient(180deg, rgba(var(--accent-rgb, 245, 166, 35), 0.08) 0%, transparent 100%), var(--surface-2)',
          border: '1px solid var(--accent)',
          shadow: '0 8px 30px rgba(var(--accent-rgb, 245, 166, 35), 0.15)',
          badge: 'For Teams',
          badgeBg: 'var(--accent)',
          textColor: 'var(--text-main)'
        };
      default:
        return {
          bg: 'var(--surface)',
          border: '1px solid var(--border)',
          shadow: 'none',
          badge: null,
          textColor: 'var(--text-muted)'
        };
    }
  };

  return (
    <div style={{ padding: '2rem 1rem', maxWidth: '1200px', margin: '0 auto', animation: 'fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: '3.5rem', position: 'relative' }}>
        <button
          onClick={onBack}
          style={{
            position: 'absolute',
            left: 0,
            top: '0.5rem',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.6rem 1rem',
            gap: '0.5rem',
            borderRadius: '100px',
            fontSize: '0.85rem',
            fontWeight: '600',
            transition: 'all 0.2s',
            backdropFilter: 'blur(8px)'
          }}
          onMouseOver={e => { e.currentTarget.style.color = 'var(--text-main)'; e.currentTarget.style.background = 'var(--surface-hover)'; }}
          onMouseOut={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'var(--surface)'; }}>
          <ArrowLeft size={16} /> Back
        </button>
        <div style={{ marginTop: '3rem' }}>
          <div style={{ display: 'inline-block', padding: '0.25rem 1rem', background: 'rgba(var(--primary-rgb, 99, 102, 241), 0.1)', color: 'var(--primary)', borderRadius: '100px', fontSize: '0.8rem', fontWeight: '700', marginBottom: '1rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Pricing Plans</div>
          <h2 style={{ fontSize: '2.75rem', fontWeight: '800', color: 'var(--text-main)', margin: '0 0 1rem 0', letterSpacing: '-0.03em', lineHeight: '1.2' }}>Scale your <span style={{ color: 'transparent', backgroundClip: 'text', WebkitBackgroundClip: 'text', backgroundImage: 'linear-gradient(90deg, var(--primary), var(--accent, #a855f7))' }}>infrastructure</span></h2>
          <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '1.1rem', maxWidth: '600px', lineHeight: '1.6' }}>
            Flexible tiers designed for side-projects, fast-growing startups, and enterprise teams.
          </p>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
        {/* Billing Cycle Toggle */}
        <div style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--surface-2)', padding: '6px', borderRadius: '100px', border: '1px solid var(--border)', margin: '0 auto', position: 'relative' }}>
          <button
            onClick={() => setBillingCycle('monthly')}
            style={{
              padding: '0.6rem 1.5rem',
              background: billingCycle === 'monthly' ? 'var(--primary)' : 'transparent',
              color: billingCycle === 'monthly' ? '#ffffff' : 'var(--text-muted)',
              border: 'none',
              borderRadius: '100px',
              cursor: 'pointer',
              fontWeight: '700',
              fontSize: '0.9rem',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              boxShadow: billingCycle === 'monthly' ? '0 2px 8px rgba(0,0,0,0.15)' : 'none'
            }}>
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            style={{
              padding: '0.6rem 1.5rem',
              background: billingCycle === 'annual' ? 'var(--primary)' : 'transparent',
              color: billingCycle === 'annual' ? '#ffffff' : 'var(--text-muted)',
              border: 'none',
              borderRadius: '100px',
              cursor: 'pointer',
              fontWeight: '700',
              fontSize: '0.9rem',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: billingCycle === 'annual' ? '0 2px 8px rgba(0,0,0,0.15)' : 'none'
            }}>
            Annually
            <span style={{ background: 'rgba(var(--success-rgb, 16, 185, 129), 0.15)', color: 'var(--success)', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '800' }}>
              SAVE 20%
            </span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem', maxWidth: '1000px', margin: '0 auto', alignItems: 'stretch' }}>
        {packages.map((pkg, i) => {
          const pkgTier = pkg.tierId || pkg.id;
          const currentPkg = packages.find(p => (p.tierId || p.id) === currentPlanId) || {
            id: currentPlanId,
            sortOrder: (currentPlanId === 'pro' || String(currentPlanId).includes('pro')) ? 1 : ((currentPlanId === 'enterprise' || String(currentPlanId).includes('enterprise')) ? 2 : 0),
            price: currentPlanPrice
          };
          const currentRank = currentPkg.sortOrder ?? 0;
          const targetRank = pkg.sortOrder ?? 0;
          const currentPrice = parseFloat(currentPkg.price) || currentPlanPrice || 0;
          const targetPrice = parseFloat(pkg.price) || 0;

          const isCurrent = ((currentPlanId === pkgTier) && (currentBillingCycle === billingCycle)) || (pkgTier === 'hobby' && (!currentPlanId || currentPlanId === 'hobby'));
          const isDowngrade = !isCurrent && (targetRank < currentRank || (targetPrice < currentPrice && !isNaN(targetPrice) && !isNaN(currentPrice) && currentPrice > 0));
          const isPro = pkg.sortOrder > 0;
          const style = getPlanStyle(pkgTier);
          const basePrice = parseFloat(pkg.price);
          const isNumeric = !isNaN(basePrice);
          const numericPrice = isNumeric ? basePrice : 0;

          const displayPrice = isNumeric
            ? (billingCycle === 'annual' ? `$${numericPrice * 10}` : `$${numericPrice}`)
            : pkg.price;

          const displayCycle = isNumeric
            ? (billingCycle === 'annual' ? '/yr' : '/mo')
            : '';

          let buttonText = 'Upgrade Plan';
          let isButtonDisabled = actionLoading || isCurrent;

          if (isCurrent) {
            buttonText = 'Current Plan';
          } else if (!isNumeric || String(pkg.price).toLowerCase() === 'custom' || pkgTier === 'enterprise') {
            buttonText = 'Contact Sales';
          } else if (isDowngrade) {
            buttonText = 'Request Downgrade';
          }

          return (
            <div key={pkgTier || i} style={{
              background: style.bg,
              border: style.border,
              borderRadius: '20px',
              padding: '1.75rem 1.5rem',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              color: 'var(--text-main)',
              boxShadow: style.shadow,
              height: '100%',
              boxSizing: 'border-box',
              zIndex: isPro ? 2 : 1,
              backdropFilter: 'blur(12px)'
            }}>
              {style.badge && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: style.badgeBg,
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '100px',
                  fontSize: '0.65rem',
                  fontWeight: '800',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  boxShadow: `0 4px 12px ${style.badgeBg}40`
                }}>
                  {style.badge}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--surface-3)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {React.cloneElement(getPlanIcon(pkgTier), { size: 20 })}
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: '800', color: 'var(--text-main)' }}>{pkg.tier}</div>
              </div>

              <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '2.5rem', fontWeight: '800', color: 'var(--text-main)', lineHeight: '1', letterSpacing: '-0.02em' }}>{displayPrice}</span>
                <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginLeft: '0.25rem', fontWeight: '500' }}>{displayCycle}</span>
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.5rem', minHeight: '40px', lineHeight: '1.5' }}>{pkg.desc}</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem', flex: 1 }}>
                {pkg.features?.map((feat, j) => (
                  <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.85rem' }}>
                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(var(--success-rgb, 16, 185, 129), 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px', flexShrink: 0 }}>
                      <Check size={10} color="var(--success)" strokeWidth={3} />
                    </div>
                    <span style={{ color: 'var(--text-main)', opacity: 0.9 }}>{feat}</span>
                  </div>
                ))}

                {pkg.limits && (Object.keys(pkg.limits).some(k => pkg.limits[k] != null)) && (
                  <>
                    <div style={{ height: '1px', background: 'var(--border)', margin: '0.5rem 0' }} />
                    <div style={{ fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Resource Limits</div>
                    {pkg.limits.maxDeployments != null && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.85rem' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(var(--primary-rgb, 99, 102, 241), 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px', flexShrink: 0 }}>
                          <Zap size={10} color="var(--primary)" strokeWidth={3} />
                        </div>
                        <span style={{ color: 'var(--text-main)', opacity: 0.9 }}>Max Deployments: <strong>{pkg.limits.maxDeployments}</strong></span>
                      </div>
                    )}
                    {pkg.limits.maxTeamMembers != null && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.85rem' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(var(--primary-rgb, 99, 102, 241), 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px', flexShrink: 0 }}>
                          <Zap size={10} color="var(--primary)" strokeWidth={3} />
                        </div>
                        <span style={{ color: 'var(--text-main)', opacity: 0.9 }}>Max Team Members: <strong>{pkg.limits.maxTeamMembers}</strong></span>
                      </div>
                    )}
                    {pkg.limits.maxTotalCpu != null && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.85rem' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(var(--primary-rgb, 99, 102, 241), 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px', flexShrink: 0 }}>
                          <Zap size={10} color="var(--primary)" strokeWidth={3} />
                        </div>
                        <span style={{ color: 'var(--text-main)', opacity: 0.9 }}>Max Total CPU: <strong>{pkg.limits.maxTotalCpu} Cores</strong></span>
                      </div>
                    )}
                    {pkg.limits.maxTotalMemory != null && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.85rem' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(var(--primary-rgb, 99, 102, 241), 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px', flexShrink: 0 }}>
                          <Zap size={10} color="var(--primary)" strokeWidth={3} />
                        </div>
                        <span style={{ color: 'var(--text-main)', opacity: 0.9 }}>Max Total RAM: <strong>{pkg.limits.maxTotalMemory}</strong></span>
                      </div>
                    )}
                    {pkg.limits.maxStorageGB != null && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.85rem' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(var(--primary-rgb, 99, 102, 241), 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px', flexShrink: 0 }}>
                          <Zap size={10} color="var(--primary)" strokeWidth={3} />
                        </div>
                        <span style={{ color: 'var(--text-main)', opacity: 0.9 }}>Max Storage: <strong>{pkg.limits.maxStorageGB} GB</strong></span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <button
                disabled={isButtonDisabled}
                onClick={() => handleSelectPlan(pkgTier)}
                style={{
                  width: '100%',
                  padding: '0.85rem',
                  borderRadius: '12px',
                  border: isButtonDisabled ? '1px solid var(--border-subtle)' : (isDowngrade ? '1px solid rgba(239, 68, 68, 0.4)' : (isPro ? 'none' : '1px solid var(--border)')),
                  background: isButtonDisabled ? 'var(--surface-3)' : (isDowngrade ? 'rgba(239, 68, 68, 0.1)' : (isPro ? 'var(--primary)' : 'var(--text-main)')),
                  color: isButtonDisabled ? 'var(--text-tertiary)' : (isDowngrade ? '#ef4444' : (isPro ? '#ffffff' : 'var(--bg)')),
                  fontWeight: '800',
                  fontSize: '0.9rem',
                  cursor: isButtonDisabled ? 'default' : 'pointer',
                  marginTop: 'auto',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  boxShadow: isPro && !isButtonDisabled && !isDowngrade ? '0 8px 24px rgba(var(--primary-rgb, 99, 102, 241), 0.3)' : 'none'
                }}>
                {buttonText}
              </button>
            </div>
          );
        })}
      </div>

      {/* Churn Mitigation & Retention Modal */}
      {showRetentionModal && downgradeTarget && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: 'var(--surface-2, #1e293b)',
            border: '1px solid var(--border, #334155)',
            borderRadius: '24px',
            maxWidth: '520px',
            width: '100%',
            padding: '2rem',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
            color: 'var(--text-main, #f8fafc)',
            position: 'relative',
            animation: 'fadeInUp 0.3s ease-out'
          }}>
            <button
              onClick={() => { setShowRetentionModal(false); setDowngradeSuccess(null); }}
              style={{
                position: 'absolute', top: '1.25rem', right: '1.25rem',
                background: 'none', border: 'none', color: 'var(--text-muted, #94a3b8)',
                cursor: 'pointer', fontSize: '1.2rem'
              }}
            >
              <X size={20} />
            </button>

            {downgradeSuccess ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
                  <Check size={32} />
                </div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', margin: '0 0 0.5rem 0' }}>{downgradeSuccess.title}</h3>
                <p style={{ color: 'var(--text-muted, #94a3b8)', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                  {downgradeSuccess.message}
                </p>
                <button
                  onClick={() => { setShowRetentionModal(false); setDowngradeSuccess(null); }}
                  style={{
                    padding: '0.75rem 2rem',
                    background: 'var(--primary, #3b82f6)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '100px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ padding: '0.6rem', borderRadius: '12px', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}>
                    <ShieldAlert size={24} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.35rem', fontWeight: '800', margin: 0 }}>Considering a Plan Downgrade?</h3>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted, #94a3b8)' }}>
                      Target: {downgradeTarget.tier || downgradeTarget.id} Plan
                    </p>
                  </div>
                </div>

                <div style={{
                  background: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  borderRadius: '12px',
                  padding: '0.85rem 1rem',
                  marginBottom: '1.25rem',
                  fontSize: '0.85rem',
                  color: '#fbbf24',
                  lineHeight: '1.5'
                }}>
                  <strong>Notice:</strong> Self-service manual downgrades are restricted to prevent accidental container termination, loss of team slots, and data loss.
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', marginBottom: '0.5rem', color: 'var(--text-main, #f8fafc)' }}>
                    Why are you considering downgrading?
                  </label>
                  <select
                    value={downgradeReason}
                    onChange={(e) => setDowngradeReason(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.65rem 0.85rem',
                      borderRadius: '10px',
                      background: 'var(--surface, #0f172a)',
                      border: '1px solid var(--border, #334155)',
                      color: 'var(--text-main, #f8fafc)',
                      fontSize: '0.85rem',
                      outline: 'none'
                    }}
                  >
                    <option value="too_expensive">It's too expensive for my budget</option>
                    <option value="unused_resources">I don't need all the current resources/slots</option>
                    <option value="missing_features">Missing features I needed</option>
                    <option value="project_ended">My project has ended or paused</option>
                    <option value="other">Other reason</option>
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowRetentionModal(false)}
                    style={{
                      padding: '0.65rem 1.25rem',
                      background: 'transparent',
                      border: '1px solid var(--border, #334155)',
                      color: 'var(--text-muted, #94a3b8)',
                      borderRadius: '100px',
                      fontWeight: '600',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleRequestDowngradeSupport}
                    disabled={submittingDowngrade}
                    style={{
                      padding: '0.65rem 1.25rem',
                      background: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#ef4444',
                      borderRadius: '100px',
                      fontWeight: '700',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem'
                    }}
                  >
                    {submittingDowngrade ? 'Submitting Request...' : 'Contact Support to Downgrade'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Contact Enterprise Sales Modal */}
      {showEnterpriseModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: 'var(--surface-2, #1e293b)',
            border: '1px solid var(--border, #334155)',
            borderRadius: '24px',
            maxWidth: '520px',
            width: '100%',
            padding: '2rem',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
            color: 'var(--text-main, #f8fafc)',
            position: 'relative',
            animation: 'fadeInUp 0.3s ease-out'
          }}>
            <button
              onClick={() => { setShowEnterpriseModal(false); setEnterpriseSuccess(null); }}
              style={{
                position: 'absolute', top: '1.25rem', right: '1.25rem',
                background: 'none', border: 'none', color: 'var(--text-muted, #94a3b8)',
                cursor: 'pointer', fontSize: '1.2rem'
              }}
            >
              <X size={20} />
            </button>

            {enterpriseSuccess ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
                  <Check size={32} />
                </div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', margin: '0 0 0.5rem 0' }}>{enterpriseSuccess.title}</h3>
                <p style={{ color: 'var(--text-muted, #94a3b8)', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                  {enterpriseSuccess.message}
                </p>
                <button
                  onClick={() => { setShowEnterpriseModal(false); setEnterpriseSuccess(null); }}
                  style={{
                    padding: '0.75rem 2rem',
                    background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '100px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ padding: '0.6rem', borderRadius: '12px', background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7' }}>
                    <Building size={24} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.35rem', fontWeight: '800', margin: 0 }}>Upgrade to Custom Enterprise</h3>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted, #94a3b8)' }}>
                      Tailored Infrastructure, Dedicated SLA & Unlimited Scale
                    </p>
                  </div>
                </div>

                <div style={{
                  background: 'rgba(168, 85, 247, 0.08)',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                  borderRadius: '12px',
                  padding: '0.85rem 1rem',
                  marginBottom: '1.25rem',
                  fontSize: '0.85rem',
                  color: '#c084fc',
                  lineHeight: '1.5'
                }}>
                  <strong>Enterprise Features:</strong> Dedicated cluster hosting, custom SLA uptime, high-frequency auto-scaling, and a 24/7 dedicated account manager.
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', marginBottom: '0.5rem', color: 'var(--text-main, #f8fafc)' }}>
                    Specific Requirements or Team Size (Optional):
                  </label>
                  <textarea
                    rows={3}
                    value={enterpriseNote}
                    onChange={(e) => setEnterpriseNote(e.target.value)}
                    placeholder="e.g. We need dedicated hosting for 20+ microservices and custom security compliance..."
                    style={{
                      width: '100%',
                      padding: '0.65rem 0.85rem',
                      borderRadius: '10px',
                      background: 'var(--surface, #0f172a)',
                      border: '1px solid var(--border, #334155)',
                      color: 'var(--text-main, #f8fafc)',
                      fontSize: '0.85rem',
                      outline: 'none',
                      resize: 'none'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowEnterpriseModal(false)}
                    style={{
                      padding: '0.65rem 1.25rem',
                      background: 'transparent',
                      border: '1px solid var(--border, #334155)',
                      color: 'var(--text-muted, #94a3b8)',
                      borderRadius: '100px',
                      fontWeight: '600',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleRequestEnterpriseSales}
                    disabled={submittingEnterprise}
                    style={{
                      padding: '0.65rem 1.25rem',
                      background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                      border: 'none',
                      color: '#ffffff',
                      borderRadius: '100px',
                      fontWeight: '700',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      boxShadow: '0 4px 15px rgba(168, 85, 247, 0.3)'
                    }}
                  >
                    {submittingEnterprise ? 'Submitting Request...' : 'Submit Sales Inquiry'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Active Subscription Period Lock Modal */}
      {showActivePeriodModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)',
          backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '24px',
            maxWidth: '480px',
            width: '100%',
            padding: '2rem',
            boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
            color: '#000000',
            position: 'relative',
            animation: 'fadeInUp 0.3s ease-out'
          }}>
            <button
              onClick={() => setShowActivePeriodModal(false)}
              style={{
                position: 'absolute', top: '1.25rem', right: '1.25rem',
                background: 'none', border: 'none', color: '#64748b',
                cursor: 'pointer', fontSize: '1.2rem'
              }}
            >
              <X size={20} />
            </button>

            <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(234, 179, 8, 0.15)', color: '#eab308', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
                <Clock size={32} />
              </div>
              <h3 style={{ fontSize: '1.4rem', fontWeight: '800', margin: '0 0 0.75rem 0', color: '#000000' }}>
                Active Plan Currently Running
              </h3>
              <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                Your current plan is active until{' '}
                <strong style={{ color: '#000000' }}>
                  {nextBillingDate ? new Date(nextBillingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'the end of your current cycle'}
                </strong>.
                You can purchase or switch to a new plan once your active 30-day or annual period completes.
              </p>

              <button
                onClick={() => setShowActivePeriodModal(false)}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'var(--primary, #6366f1)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '100px',
                  fontWeight: '700',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Understood
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Plans;
